import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";

import type {
    LinkBlock,
    MasonryHeroBlock as MasonryHeroBlockValue,
} from "@reactivated";

import { stripHtmlToPlainText } from "../../utils/text";
import { Flair } from "../Flair";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

const linkHref = (link: Immutable<LinkBlock>): string =>
    link.page?.url ?? link.external_url ?? "#";

export const MasonryHeroBlock = (
    props: Immutable<{ value: MasonryHeroBlockValue }>,
): React.JSX.Element => {
    // Every field is optional in the CMS, so each is guarded here rather than
    // assumed: an editor can save a partially-filled block at any time.
    const { title, link_slides_title: linkSlidesTitle } = props.value;
    const auxSlide = props.value.aux_slide;
    const eligibleHeroSlides = (props.value.hero_slides ?? [])
        .map((slide, i) => ({ slide, i }))
        .filter(
            ({ slide }) =>
                slide.image && (!slide.promo || slide.promo.is_promo_active),
        );
    const desktopVisible = new Set<number>();
    for (const { slide, i } of eligibleHeroSlides) {
        if (slide.desktop && desktopVisible.size < 2) {
            desktopVisible.add(i);
        }
    }
    if (desktopVisible.size === 0 && eligibleHeroSlides.length > 0) {
        desktopVisible.add(eligibleHeroSlides[0].i);
    }
    const sectionLabel = stripHtmlToPlainText(title ?? "");

    return (
        <section className={styles.root} aria-label={sectionLabel || undefined}>
            {title && (
                <RichText tagName="h2" className={styles.title} html={title} />
            )}
            <div className={styles.masonry}>
                <div className={styles.heroSlides}>
                    {eligibleHeroSlides.map(({ slide, i }) => {
                        if (slide.image) {
                            const content = (
                                <>
                                    <div
                                        className={styles.heroSlideImageWrapper}
                                    >
                                        <img
                                            className={styles.heroSlideImage}
                                            src={slide.image.url}
                                            alt={slide.image.title}
                                        />
                                    </div>
                                    <div className={styles.heroSlideGradient} />
                                    <div className={styles.heroSlideContent}>
                                        {slide.flair && (
                                            <div
                                                className={
                                                    styles.heroSlideFlair
                                                }
                                            >
                                                <Flair
                                                    flair={slide.flair}
                                                    inline
                                                />
                                            </div>
                                        )}
                                        <h3 className={styles.heroSlideTitle}>
                                            {slide.title}
                                        </h3>
                                        <p className={styles.heroSlideBody}>
                                            {slide.body}
                                        </p>
                                        {slide.callout && (
                                            <p
                                                className={
                                                    styles.heroSlideCallout
                                                }
                                            >
                                                {slide.callout}
                                            </p>
                                        )}
                                    </div>
                                </>
                            );
                            return (
                                <div
                                    key={i}
                                    className={classNames(styles.heroSlide, {
                                        [styles.heroSlideMobileOnly]:
                                            !desktopVisible.has(i),
                                    })}
                                >
                                    {slide.link ? (
                                        <a
                                            className={styles.heroSlideLink}
                                            href={linkHref(slide.link)}
                                            aria-label={
                                                slide.link.display_name ??
                                                undefined
                                            }
                                        >
                                            {content}
                                        </a>
                                    ) : (
                                        content
                                    )}
                                </div>
                            );
                        }
                        return null;
                    })}
                    {auxSlide && (
                        <div
                            className={classNames(
                                styles.heroSlide,
                                styles.heroAuxSlide,
                            )}
                        >
                            {auxSlide.image && (
                                <div
                                    className={styles.heroAuxSlideImageWrapper}
                                >
                                    <img
                                        className={styles.heroAuxSlideImage}
                                        src={auxSlide.image.url}
                                        alt={auxSlide.image.title}
                                    />
                                </div>
                            )}
                            {auxSlide.body && (
                                <RichText
                                    className={styles.heroAuxSlideBody}
                                    html={auxSlide.body}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.otherSlides}>
                    {auxSlide && (
                        <div className={styles.auxSlide}>
                            <div className={styles.auxSlideImageWrapper}>
                                {auxSlide.image && (
                                    <img
                                        className={styles.auxSlideImage}
                                        src={auxSlide.image.url}
                                        alt={auxSlide.image.title}
                                    />
                                )}
                            </div>
                            {auxSlide.body && (
                                <RichText
                                    className={styles.auxSlideBody}
                                    html={auxSlide.body}
                                />
                            )}
                        </div>
                    )}
                    <div className={styles.linkSlides}>
                        {linkSlidesTitle && (
                            <h3 className={styles.linkSlidesTitle}>
                                {linkSlidesTitle}
                            </h3>
                        )}
                        <div className={styles.linkSlidesGrid}>
                            {(props.value.link_slides ?? []).map((slide, i) => {
                                const tileContent = (
                                    <>
                                        {slide.flair && (
                                            <div
                                                className={
                                                    styles.linkSlideFlair
                                                }
                                            >
                                                <Flair
                                                    flair={slide.flair}
                                                    inline
                                                />
                                            </div>
                                        )}
                                        <div
                                            className={
                                                styles.linkSlideImageWrapper
                                            }
                                        >
                                            {slide.image && (
                                                <img
                                                    className={
                                                        styles.linkSlideImage
                                                    }
                                                    src={slide.image.url}
                                                    alt={slide.image.title}
                                                />
                                            )}
                                        </div>
                                        <p className={styles.linkSlideBody}>
                                            {slide.body}
                                        </p>
                                    </>
                                );
                                return (
                                    <div key={i} className={styles.linkSlide}>
                                        {slide.link ? (
                                            <a
                                                className={styles.linkSlideLink}
                                                href={linkHref(slide.link)}
                                                aria-label={
                                                    slide.link.display_name ??
                                                    undefined
                                                }
                                            >
                                                {tileContent}
                                            </a>
                                        ) : (
                                            tileContent
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
